import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { useMissionAccess } from '../../components/Access/hooks/useMissionAccess.hook';
import {
  PATH_MISSION_DEFAULT,
  PATH_MISSION_QUOTATION_DEFAULT,
  PATH_MISSION_QUOTATION_INIT,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { Calculator } from '../../components/Illustrations/Calculator';
import { SplashScreen } from '../../components/SplashScreen';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionContext } from '../../contexts/MissionContext/hooks/useMissionContext.hook';
import { useQuotationContext } from '../../contexts/QuotationContext/hooks/useQuotationContext.hook';
import { QuotationContextStatus } from '../../contexts/QuotationContext/types/quotation-context.type';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { MissionQuotationInit } from './Init';
import { MissionQuotationReady } from './Ready';

interface Props {
  readonly isInPrintMode?: boolean;
}

export const MissionQuotationPage = ({
  isInPrintMode = false,
}: Readonly<Props>) => {
  const navigate = useNavigate();

  const { folderId, missionId, mission } = useMissionContext();
  const access = useMissionAccess(missionId, mission);
  const missionWording = useMissionSpaceWording();
  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();
  const quotationContext = useQuotationContext();

  const { paths: missionPaths, currentPathKey } = useBuildPaths(
    {
      default: PATH_MISSION_DEFAULT,
      quotation: PATH_MISSION_QUOTATION_DEFAULT,
      initQuotation: PATH_MISSION_QUOTATION_INIT,
    },
    { folderId, missionId },
  );

  const goToInitPage = useCallback(
    () => void navigate(missionPaths.initQuotation),
    [navigate, missionPaths.initQuotation],
  );

  const goToQuotationPage = useCallback(
    () => void navigate(missionPaths.quotation),
    [navigate, missionPaths.quotation],
  );

  const isInitQuotationPath = currentPathKey === 'initQuotation';

  const beforeTitleContent = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            isInitQuotationPath
              ? 'Initialisation du Devis'
              : `Fiche Devis ${missionWording.ofName}`,
            mission?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          ...missionBreadcrumbPaths,
          {
            to: missionPaths.quotation,
            label: 'devis',
            key: 'quotation',
          },
        ]}
      />
    </>
  );

  const titleContent = (
    <PageTitle
      code={mission?.code}
      title={
        isInitQuotationPath
          ? 'Initialisation du Devis'
          : `Fiche Devis ${missionWording.ofName}`
      }
    />
  );

  if (
    quotationContext.status === QuotationContextStatus.Ready &&
    isInitQuotationPath
  ) {
    return (
      <>
        {beforeTitleContent}
        {titleContent}
        <SplashScreen
          action={{ label: 'Consulter le Devis', onClick: goToQuotationPage }}
          illustration={<Calculator />}
          message={`Le devis ${missionWording.ofName} a déjà été initialisé.`}
        />
      </>
    );
  }

  if (quotationContext.status === QuotationContextStatus.Ready) {
    return <MissionQuotationReady isInPrintMode={isInPrintMode} />;
  }

  if (quotationContext.status === QuotationContextStatus.NotInitialized) {
    return (
      <>
        {beforeTitleContent}
        {titleContent}
        {isInitQuotationPath ? (
          <MissionQuotationInit />
        ) : (
          <SplashScreen
            action={{
              label: 'Initialiser le Devis',
              onClick: goToInitPage,
              gate: access.initQuotation,
            }}
            illustration={<Calculator />}
            message={`Vous n'avez pas encore initialisé le devis ${missionWording.ofName}. Commencez le travail !`}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Loading />
      {beforeTitleContent}
      {titleContent}
    </>
  );
};
